import React, { useCallback, useMemo } from 'react';
import { Form, Input, Space } from 'antd';
import { NumericFormat } from 'react-number-format';
import { useTranslation } from 'react-i18next';

interface AmountInputProps {
  index: number;
  form: any;
  onValidateAmount: (index: number) => Promise<void>;
  disabled?: boolean;
}

/**
 * Reusable Amount Input Component
 *
 * Handles NOK and Øre input with validation and formatting.
 */
export const AmountInput: React.FC<AmountInputProps> = ({
  index,
  form,
  onValidateAmount,
  disabled = false,
}) => {
  const { t } = useTranslation();
  // Memoize numeric format props to prevent re-creation
  const numericFormatProps = useMemo(
    () => ({
      allowNegative: false,
      decimalScale: 0,
      valueIsNumericString: true,
      customInput: Input,
      onFocus: (event: React.FocusEvent<HTMLInputElement>) =>
        event.target.select(),
    }),
    []
  );

  const handleNokBlur = useCallback(
    (value: string) => {
      if (value === '') {
        form.setFieldValue(['expenses', index, 'nok'], '0');
      } else {
        const num = parseInt(value.replace(' ', ''), 10);
        form.setFieldValue(['expenses', index, 'nok'], num.toString());
      }
      // Trigger validation on the amount field to use onValidateAmount function
      form.validateFields([['expenses', index, 'amount']]);
    },
    [form, index]
  );

  const handleOreBlur = useCallback(
    (value: string) => {
      if (value === '') {
        form.setFieldValue(['expenses', index, 'ore'], '00');
      } else if (value.length === 1) {
        form.setFieldValue(['expenses', index, 'ore'], `${value}0`);
      } else {
        form.setFieldValue(['expenses', index, 'ore'], value);
      }
      // Trigger validation on the amount field to use onValidateAmount function
      form.validateFields([['expenses', index, 'amount']]);
    },
    [form, index]
  );

  return (
    <Form.Item
      name={['expenses', index, 'amount']}
      label={`${t('Amount')} (NOK)`}
      rules={[
        () => ({
          validator: async () => {
            await onValidateAmount(index);
          },
        }),
      ]}
    >
      <Space.Compact>
        <Form.Item
          name={['expenses', index, 'nok']}
          style={{ marginBottom: 0 }}
        >
          <NumericFormat
            {...numericFormatProps}
            allowLeadingZeros={false}
            style={{ width: '75px' }}
            thousandSeparator=" "
            isAllowed={(values) =>
              values.floatValue === undefined || values.floatValue <= 999999
            }
            onBlur={(event) => handleNokBlur(event.target.value)}
            disabled={disabled}
          />
        </Form.Item>
        <span style={{ marginTop: 8 }}>&nbsp;.&nbsp;</span>
        <Form.Item
          name={['expenses', index, 'ore']}
          style={{ marginBottom: 0 }}
        >
          <NumericFormat
            {...numericFormatProps}
            allowLeadingZeros={true}
            maxLength={2}
            style={{ width: 40, textAlign: 'center' }}
            isAllowed={(values) =>
              values.floatValue === undefined || values.floatValue <= 99
            }
            onBlur={(event) => handleOreBlur(event.target.value)}
            disabled={disabled}
          />
        </Form.Item>
      </Space.Compact>
    </Form.Item>
  );
};
